/**
 * Public portal config (OCP-656) - loaded once at boot from
 * `GET /v1/portal/config` (no session), outside React so the existing
 * fetch-mock sequences in tests stay untouched. OCP-668 (launch): the
 * languages and every AGB V4 / orboto Token surface are always on - the
 * former `portal.agb_v3_released` switch is gone. `localesEnabled` stays as
 * a boolean the api reports (always true) so the toggle logic has one source.
 */
import { useSyncExternalStore } from 'react';

import { applyLocale, currentLocale } from '../i18n';
import { api } from './api';

export interface PortalConfig {
  localesEnabled: boolean;
}

let state: PortalConfig = { localesEnabled: true };
const listeners = new Set<() => void>();

export function getPortalConfig(): PortalConfig {
  return state;
}

export function setPortalConfig(next: Partial<PortalConfig>): void {
  state = { ...state, ...next };
  for (const l of listeners) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function usePortalConfig(): PortalConfig {
  return useSyncExternalStore(subscribe, getPortalConfig, getPortalConfig);
}

/** Boot-time load; an unreachable config keeps the launch default (languages on). */
export async function loadPortalConfig(): Promise<void> {
  try {
    const cfg = await api<{ locales: { enabled: boolean } }>('/v1/portal/config');
    setPortalConfig({ localesEnabled: cfg.locales.enabled !== false });
    if (cfg.locales.enabled === false && currentLocale() !== 'en') await applyLocale('en');
  } catch {
    setPortalConfig({ localesEnabled: true });
  }
}
